import React, { useState, useEffect } from 'react';
import { userAPI, authAPI } from '../../services/api';

// F3: normalize roles — API returns [{_id, name}], session returns ["ROLE_ADMIN"] strings
const roleName = (role) => (typeof role === 'string' ? role.replace('ROLE_', '').toLowerCase() : role?.name || '');
const roleId = (role) => (typeof role === 'string' ? role : role?._id || role);
const roleBadgeClass = (name) => {
  if (name === 'admin') return 'badge-error';
  if (name === 'moderator') return 'badge-warning';
  return 'badge-info';
};

const AVAILABLE_ROLES = ['user', 'moderator', 'admin'];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', email: '', roles: [] });
  const [passwordData, setPasswordData] = useState({ userId: '', newPassword: '', confirmPassword: '' });

  const currentUser = authAPI.getCurrentUser();

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response);
    } catch {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    // Normalize to plain role name strings for the form
    setFormData({
      username: user.username,
      email: user.email,
      roles: (user.roles || []).map(roleName),
    });
    setShowModal(true);
  };

  const handlePasswordChange = (user) => {
    setPasswordData({ userId: user._id, newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await userAPI.updateUserRoles(editingUser._id, formData.roles);
      setSuccess('User roles updated.');
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setError('Passwords do not match');
    }
    if (passwordData.newPassword.length < 6) {
      return setError('Password must be at least 6 characters');
    }
    try {
      await userAPI.changeUserPassword(passwordData.userId, passwordData.newPassword);
      setSuccess('Password changed.');
      setShowPasswordModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    }
  };

  const handleDelete = async (user) => {
    if (user._id === currentUser?.id) return setError('You cannot delete your own account');
    if (!window.confirm(`Delete user "${user.username}"?`)) return;
    try {
      await userAPI.delete(user._id);
      setSuccess('User deleted.');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ username: '', email: '', roles: [] });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="loading loading-spinner loading-md text-blue-500"></span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">User Management</h2>
          <p className="text-sm text-slate-500 mt-0.5">{users.length} accounts registered</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error py-2 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          {success}
        </div>
      )}

      {/* Table */}
      <div className="card bg-base-100 border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-sm table-zebra w-full">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th>User</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{user.username}</div>
                        {user._id === currentUser?.id && (
                          <div className="text-xs text-blue-500">You</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-slate-600 text-sm">{user.email}</td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {(user.roles || []).length > 0
                        ? (user.roles).map((role) => (
                          <span
                            key={roleId(role)}
                            className={`badge badge-sm capitalize ${roleBadgeClass(roleName(role))}`}
                          >
                            {roleName(role)}
                          </span>
                        ))
                        : <span className="text-slate-400 text-xs">—</span>
                      }
                    </div>
                  </td>
                  <td className="text-slate-500 text-sm">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        className="btn btn-ghost btn-xs"
                        title="Edit roles"
                        onClick={() => handleEdit(user)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button
                        className="btn btn-ghost btn-xs text-warning"
                        title="Change password"
                        onClick={() => handlePasswordChange(user)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                      </button>
                      {user._id !== currentUser?.id && (
                        <button
                          className="btn btn-ghost btn-xs text-error"
                          title="Delete user"
                          onClick={() => handleDelete(user)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-400 text-sm">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Roles Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Edit User: {editingUser?.username}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label pb-1"><span className="label-text font-medium">Username</span></label>
                <input type="text" value={formData.username} className="input input-bordered input-sm" disabled />
                <label className="label pt-1"><span className="label-text-alt text-slate-400">Cannot be changed</span></label>
              </div>
              <div className="form-control">
                <label className="label pb-1"><span className="label-text font-medium">Email</span></label>
                <input type="email" value={formData.email} className="input input-bordered input-sm" disabled />
              </div>
              <div className="form-control">
                <label className="label pb-1"><span className="label-text font-medium">Roles</span></label>
                <div className="flex gap-4 flex-wrap">
                  {AVAILABLE_ROLES.map((role) => (
                    <label key={role} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-sm"
                        checked={formData.roles.includes(role)}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            roles: e.target.checked
                              ? [...prev.roles, role]
                              : prev.roles.filter(r => r !== role),
                          }));
                        }}
                      />
                      <span className="capitalize text-sm">{role}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal-action">
                <button type="button" className="btn btn-ghost btn-sm" onClick={resetModal}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm">Save Changes</button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop"><button type="button" onClick={resetModal}>close</button></form>
        </dialog>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Change Password</h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label pb-1"><span className="label-text font-medium">New Password</span></label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="input input-bordered input-sm"
                  placeholder="Min. 6 characters"
                  required minLength={6}
                />
              </div>
              <div className="form-control">
                <label className="label pb-1"><span className="label-text font-medium">Confirm Password</span></label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="input input-bordered input-sm"
                  placeholder="Repeat password"
                  required
                />
              </div>
              <div className="modal-action">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowPasswordModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-warning btn-sm">Change Password</button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop"><button type="button" onClick={() => setShowPasswordModal(false)}>close</button></form>
        </dialog>
      )}
    </div>
  );
};

export default UserManagement;
